import { useEffect, useMemo } from 'react';

import * as cache from '@snowit/cache';
import useLanguage from '@snowit/system/useLanguage';
import { navGroups } from '@snowit/wouter';

import { create } from 'zustand';

type QuickJumpEntry = {
  path: string;
  label: string;
  group: string;
  index: number;
};

type BookmarkList = Set<string>;

type BookmarksStore = {
  bookmarks: BookmarkList;
  viewOnlyBookmarks: boolean;
  hydrate: () => Promise<void>;
  toggleView: () => Promise<void>;
  toggleBookmark: (input: string) => Promise<void>;
};

const CACHE_BOOKMARKS = 'bookmarks';
const CACHE_VIEW = 'bookmarks_view';

let hydration: Promise<void> | undefined;

const useBookmarksStore = create<BookmarksStore>((set, get) => ({
  bookmarks: new Set(),
  viewOnlyBookmarks: false,
  hydrate: () => {
    hydration ??= Promise.all([
      cache.get<BookmarkList | string[]>(CACHE_BOOKMARKS),
      cache.get<boolean>(CACHE_VIEW),
    ]).then(([bookmarks, viewOnlyBookmarks]) => {
      set({
        bookmarks: new Set(bookmarks ?? []),
        viewOnlyBookmarks: viewOnlyBookmarks ?? false,
      });
    });
    return hydration;
  },
  toggleBookmark: async (input) => {
    const bookmarks = new Set(get().bookmarks);
    if (bookmarks.has(input)) {
      bookmarks.delete(input);
    } else {
      bookmarks.add(input);
    }
    await cache.set(CACHE_BOOKMARKS, bookmarks, { forever: true });
    set({ bookmarks });
  },
  toggleView: async () => {
    const viewOnlyBookmarks = !get().viewOnlyBookmarks;
    set({ viewOnlyBookmarks });
    await cache.set(CACHE_VIEW, viewOnlyBookmarks, { forever: true });
  },
}));

const useBookmarks = () => {
  const { t } = useLanguage();
  const bookmarks = useBookmarksStore((state) => state.bookmarks);
  const viewOnlyBookmarks = useBookmarksStore((state) => state.viewOnlyBookmarks);
  const hydrate = useBookmarksStore((state) => state.hydrate);
  const toggleView = useBookmarksStore((state) => state.toggleView);
  const toggleBookmark = useBookmarksStore((state) => state.toggleBookmark);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const routes = useMemo((): QuickJumpEntry[] => {
    const paths = new Set<string>();

    return navGroups.reduce((memo, group, index) => {
      const routes: QuickJumpEntry[] = group.routes
        .filter((route) => {
          if (route.path === ('/' as any) || paths.has(route.path)) return false;
          paths.add(route.path);
          return true;
        })
        .map((route) => ({
          path: route.path,
          label: t(`lzy.${route.label}`),
          group: t(`lzy.routes.group.${group.id}`),
          index,
        }));
      return [...memo, ...routes];
    }, [] as QuickJumpEntry[]);
  }, [t]);

  return { bookmarks, routes, viewOnlyBookmarks, toggleView, toggleBookmark };
};

export default useBookmarks;
